import DSAuth from '../DSAuth';
import formsAuthWebviewHandler from '../forms/FormsAuthWebViewHandler';
import { AuthChoice } from '../data/AuthChoice';
import { GenericRequest, HTTPFetchResponse } from '../data/AuthTypes';
import {
	stringConstants,
	primaryTokenStatusConstants,
	headerConstants,
} from '../../AuthConstants';
import AuthUtils from '../../AuthUtils';
import Utils from '../../../utils/Utils';
import { FormsAuthenticateResponseParser } from '../parser/FormsAuthenticateResponseParser';
import ExplicitFormsHelper from '../ExplicitFormsHelper';
import { FormsAuthenticateResponse } from '../data/FormsAuthenticateResponse';
import {
	PrimaryTokenFetchFailure,
	RequestTokenResponseParseError,
} from '../../AuthErrors';
import { HeadersJson } from '../data/AuthTypes';
import { HeaderParser } from '../parser/HeaderParser';
import { SentryLogger } from '../../../sentryLogging/SentryLogger';
import { Severity } from '@sentry/browser';
import { config } from '../../AuthConfig';
import CredentialsManager from '../forms/CredentialsManager';
import { HttpStatusCodes } from '../../../utils/http/HttpConstants';
import { Logger } from '../../Logger';
import { AotCategory, logConstants } from '../../../utils/Constants';
import {
	Authenticator,
	AuthenticateError,
	isTokenResponse,
	logEvent,
	analyticsConstants,
} from './Authenticator';
import authWebviewHandler from '../AuthWebViewHandler';
import { CloseFormsReason } from 'src/AccountConfig';

export default class FormsAuthenticator implements Authenticator {
	private readonly StateContextKey = 'StateContext';
	private readonly WebviewCredentialType = 'webview';
	private hasWebviewRequirement = false;
	private isAuthFormShown = false;
	private credentialsManager: CredentialsManager;
	private explicitFormsHelper = new ExplicitFormsHelper(
		new FormsAuthenticateResponseParser()
	);

	async getToken(
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		SentryLogger.getInstance().addBreadCrumb({
			category: 'FormsAuthenticator',
			level: Severity.Info,
			message: 'getToken for FormsAuthenticator',
		});

		this.hasWebviewRequirement = false;

		const authResponse = await this.getAuthenticateResponse(
			id,
			referenceUri,
			authProtocol,
			authProtocolsRequest
		);
		this.validateResultMoreInfo(authResponse);

		this.credentialsManager = await CredentialsManager.createInstance(
			authProtocol.getLocation(),
			referenceUri,
			authResponse.getRequirements()
		);

		this.isAuthFormShown = false;
		const primaryTokenResponse = await this.fetchTokenUsingSavedCredentials(
			authResponse,
			id,
			referenceUri,
			transactionId
		);
		if (primaryTokenResponse) {
			if (isTokenResponse(primaryTokenResponse)) {
				logEvent(
					this.hasWebviewRequirement
						? analyticsConstants.LABEL_DSFORMSVARIANTAUTHIMPL
						: analyticsConstants.LABEL_DSFORMSAUTHIMPL,
					1
				);
			}
			return primaryTokenResponse;
		}

		const formResponse = await this.getFormResponse(
			authResponse,
			id,
			referenceUri,
			transactionId
		);

		try {
			return await this.processFormResponseAndGetToken(
				formResponse,
				authResponse.getPostBack(),
				authResponse.getCancelPostback(),
				authResponse.getStateContext(),
				referenceUri,
				id,
				transactionId
			);
		} catch (e) {
			this.closeForm(id, 'error');
			throw e;
		}
	}

	private async fetchTokenUsingSavedCredentials(
		authResponse: FormsAuthenticateResponse,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<HTTPFetchResponse> {
		let postData;
		if (config.CredentialInsertionEnabled) {
			postData = await this.credentialsManager.getPostDataWithFetchCredentialFromNative(
				authResponse.getStateContext()
			);
		} else {
			postData = await this.credentialsManager.getPostData(
				authResponse.getStateContext()
			);
		}

		if (postData) {
			SentryLogger.getInstance().addBreadCrumb({
				category: 'FormsAuthenticator',
				level: Severity.Info,
				message: 'Using stored credentials to fetch primary token',
			});
			Logger.getInstance().write(
				logConstants.INFO,
				`FormsAuthenticator: Using stored credentials to fetch primary token.`
			);

			const primaryTokenResponse = await this.fetchPrimaryToken(
				postData,
				authResponse.getPostBack(),
				transactionId,
				referenceUri,
				id
			);

			if (this.isPrimaryTokenReceived(primaryTokenResponse.headers)) {
				Logger.getInstance().writeAot(
					AotCategory.Info,
					`Fetched primary token using saved credentials`
				);
				return primaryTokenResponse;
			} else {
				Logger.getInstance().writeAot(
					AotCategory.Warning,
					`Failed to fetch primary token using saved credentials`
				);
				await this.credentialsManager.onAuthenticationFailed();
				return null;
			}
		}
	}

	private async getFormResponse(
		authResponse: FormsAuthenticateResponse,
		id: string,
		referenceUri: string,
		transactionId: string
	): Promise<string> {
		const webviewRequirement = authResponse
			.getRequirements()
			.find((requirement) => requirement.credential.type === this.WebviewCredentialType);
		if (webviewRequirement) {
			this.hasWebviewRequirement = true;
			const webviewResponse = await authWebviewHandler.start(
				webviewRequirement.credential.webViewData.startUrl,
				referenceUri,
				id,
				stringConstants.AUTHWEBVIEW,
				transactionId
			);
			return webviewResponse === primaryTokenStatusConstants.ERROR_CANCEL_LOGIN
				? webviewResponse
				: `${webviewRequirement.credential.id}=${webviewResponse}`;
		}

		const prepopulatedCredentials = this.credentialsManager.getPrepopulatedCredentials();
		this.isAuthFormShown = true;
		return formsAuthWebviewHandler.showForm(
			id,
			this.credentialsManager.getUpdatedRequirements(),
			prepopulatedCredentials,
			referenceUri,
			this.getUITimeOut(authResponse)
		);
	}

	// Return timeout value in seconds.
	private getUITimeOut(authResponse: FormsAuthenticateResponse): number {
		const timeout = authResponse.getTimeOut();
		if (timeout) {
			return timeout;
		}
		return config.uiInactivityTimeOutForOnPremInSeconds;
	}

	private async getAuthenticateResponse(
		id: string,
		referenceUri: string,
		authProtocol: AuthChoice,
		authProtocolsRequest: GenericRequest
	): Promise<FormsAuthenticateResponse> {
		const explicitFormsLocation = authProtocol.getLocation();
		const explicitFormsResponse =
			(await this.explicitFormsHelper.getExplicitFormsRequestResponse(
				authProtocolsRequest,
				explicitFormsLocation,
				referenceUri,
				id,
				true,
				{ [headerConstants.ACCEPT_LANGUAGE]: config.locale }
			)) as FormsAuthenticateResponse;

		return new FormsAuthenticateResponse(
			explicitFormsResponse.getStatus(),
			explicitFormsResponse.getResult(),
			Utils.getInstance().getOrigin(explicitFormsLocation) +
				explicitFormsResponse.getPostBack(),
			explicitFormsResponse.getCancelPostback()
				? Utils.getInstance().getOrigin(explicitFormsLocation) +
				  explicitFormsResponse.getCancelPostback()
				: '',
			explicitFormsResponse.getStateContext(),
			null,
			explicitFormsResponse.getRequirements(),
			explicitFormsResponse.getTimeOut()
		);
	}

	private async processFormResponseAndGetToken(
		formResponse: string,
		postBackUrl: string,
		cancelUrl: string,
		stateContext: string,
		referenceUri: string,
		id: string,
		transactionId: string
	): Promise<HTTPFetchResponse | AuthenticateError> {
		while (true) {
			if (formResponse === primaryTokenStatusConstants.ERROR_CANCEL_LOGIN) {
				this.closeForm(id, 'cancelled');
				await this.cancelLogin(cancelUrl, stateContext, referenceUri, transactionId);
				return AuthenticateError.CancelledByUser;
			} else {
				const postData = `${this.StateContextKey}=${stateContext}&${formResponse}`;
				const primaryTokenResponse = await this.fetchPrimaryToken(
					postData,
					postBackUrl,
					transactionId,
					referenceUri,
					id
				);

				if (this.isPrimaryTokenReceived(primaryTokenResponse.headers)) {
					await this.credentialsManager.onAuthenticationSucceeded(postData);
					this.closeForm(id, 'success');
					logEvent(
						this.hasWebviewRequirement
							? analyticsConstants.LABEL_DSFORMSVARIANTAUTHIMPL
							: analyticsConstants.LABEL_DSFORMSAUTHIMPL,
						1
					);
					return primaryTokenResponse;
				} else if (this.isAuthFormShown) {
					await this.credentialsManager.onAuthenticationFailed();
					const authProtocolsResponse =
						this.explicitFormsHelper.processExplicitFormsResponse(
							primaryTokenResponse,
							id
						) as FormsAuthenticateResponse;

					this.validateResultMoreInfo(authProtocolsResponse);
					this.credentialsManager.updateRequirements(
						authProtocolsResponse.getRequirements()
					);

					formResponse = await formsAuthWebviewHandler.updateForm(
						id,
						this.credentialsManager.getUpdatedRequirements()
					);
				} else {
					throw new PrimaryTokenFetchFailure(
						new Error('Primary token response does not contain expected Content-Type.')
					);
				}
			}
		}
	}

	private async fetchPrimaryToken(
		stateContext: string,
		postBackUrl: string,
		transactionId: string,
		referenceUri: string,
		id: string
	): Promise<HTTPFetchResponse> {
		const headers = DSAuth.getInstance().addBasicHeaders(
			AuthUtils.getInstance().addLabelAndCredTypesToRequestHeader(
				{
					Accept:
						headerConstants.AUTH_RESPONSE_CONTENT_TYPE +
						',' +
						headerConstants.TOKEN_RESPONSE_CONTENT_TYPE,
					[headerConstants.CONTENT_TYPE]: headerConstants.REQUEST_CONTENT_TYPE,
					[headerConstants.ACCEPT_LANGUAGE]: config.locale,
				},
				true
			),
			transactionId
		);

		let primaryTokenResponse;
		try {
			primaryTokenResponse = await DSAuth.getInstance().fetch(
				stringConstants.POST,
				postBackUrl,
				headers,
				stateContext,
				referenceUri
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`${id}: PRIMARY_TOKEN_REQUEST_FAILURE: ${e.message}`
			);
			throw new PrimaryTokenFetchFailure(e);
		}

		if (primaryTokenResponse.statusCode != HttpStatusCodes.HTTP_STATUS_OK) {
			Logger.getInstance().writeAot(
				AotCategory.Error,
				`Failed to fetch primary token - unexpected HTTP response status code: ${primaryTokenResponse.statusCode}`
			);
			throw new PrimaryTokenFetchFailure(
				new Error(
					'Response status for primary token request is ' +
						primaryTokenResponse.statusCode
				)
			);
		}
		return primaryTokenResponse;
	}

	private isPrimaryTokenReceived(primaryTokenResponseHeaders: HeadersJson): boolean {
		if (
			HeaderParser.getInstance().containsMediaTypeinContentType(
				primaryTokenResponseHeaders,
				headerConstants.TOKEN_RESPONSE_CONTENT_TYPE
			)
		) {
			return true;
		}

		if (
			HeaderParser.getInstance().containsMediaTypeinContentType(
				primaryTokenResponseHeaders,
				headerConstants.AUTH_RESPONSE_CONTENT_TYPE
			)
		) {
			return false;
		}

		throw new RequestTokenResponseParseError(
			Error('Invalid Content-Type header in primary token response')
		);
	}

	private async cancelLogin(
		cancelUrl: string,
		stateContext: string,
		referenceUri: string,
		transactionId: string
	) {
		if (!cancelUrl) {
			Logger.getInstance().write(
				logConstants.INFO,
				`FormsPrimaryTokenService cancelLogin: no cancelUrl.`
			);
			return;
		}

		Logger.getInstance().write(logConstants.INFO, `Cancelling forms login.`);
		SentryLogger.getInstance().addBreadCrumb({
			category: 'FormsPrimaryTokenService',
			level: Severity.Info,
			message: 'Cancelling forms login.',
		});

		try {
			const cancelRequest = {
				method: stringConstants.POST,
				headers: DSAuth.getInstance().addBasicHeaders(
					{
						Accept: headerConstants.AUTH_RESPONSE_CONTENT_TYPE,
						[headerConstants.CONTENT_TYPE]: headerConstants.REQUEST_CONTENT_TYPE,
					},
					transactionId
				),
				body: `${this.StateContextKey}=${stateContext}`,
				url: cancelUrl,
			};

			await DSAuth.getInstance().fetch(
				cancelRequest.method,
				cancelRequest.url,
				cancelRequest.headers,
				cancelRequest.body,
				referenceUri
			);
		} catch (e) {
			Logger.getInstance().write(
				logConstants.ERROR,
				`Failed to send Cancel request: ${e.message}`
			);
		}
	}

	private validateResultMoreInfo(authResponse: FormsAuthenticateResponse): void {
		if (authResponse.getResult() !== 'more-info') {
			throw new Error(`Unexpected forms result - ${authResponse.getResult()}`);
		}
	}

	private closeForm(id: string, reason: CloseFormsReason): void {
		if (this.isAuthFormShown) {
			formsAuthWebviewHandler.closeForm(id, reason);
		}
	}
}
